import produce from 'immer';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import {
  Range,
  Upload,
  UploadProviderOptions,
  UploadSession,
  UploadStatus,
  queuedUploads,
  readFileInChunks
} from './utils';
import { UploadProviderContext, useIdentifyInterruptedUploads } from './hooks';

type WritableDraft<T> = T;

export interface UploadProviderInfo {
  sessions: UploadSession[];
  uploadStarted?: Date;
}

export interface UploadProviderActions {
  uploadFiles(files: File[] | FileList, args?: Record<string, any>): Promise<void>;
  cancelUploadSession(id: string): Promise<void>;
  hideUploadSession(id: string): void;
  clearCompletedSessions(): void;
  retryUploadSession(id: string, file: File): Promise<void>;
  retryUploadSessions(files: File[] | FileList): Promise<void>;
  identifyInterruptedUploads(): void;
  hideOldUploads(): void;
}

interface UploadProviderProps {
  createUploadSession(name: string, file: File, args?: Record<string, any>): Promise<UploadSession>;
  uploadChunk(session: UploadSession, chunk: ArrayBuffer, range: Range): Promise<void>;
  uploadFile(session: UploadSession, file: File): Promise<void>;
  completeUpload(session: UploadSession): Promise<any>;
  destroyUpload(session: UploadSession): Promise<void>;
  options: UploadProviderOptions;
}

// These are immer actions, so we can mutate the draft directly
// We can do this inline as we've done in some of the service but it's often easier to read if we extract them out

const changeStatus = (id: string, status: UploadStatus) =>
  produce((draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    console.log(`Changing status of ${id} to ${status}`);
    const session = draft.sessions.find(s => s.id === id);
    if (!session) {
      console.warn(`No session found with id ${id}`);
      return;
    }
    session.status = status;
    console.log(`Status of ${id} is now ${status}`);
  });

const updateProgress = (id: string, range: Range, duration: number) =>
  produce((draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    const session = draft.sessions.find(s => s.id === id);
    if (!session) return;
    if (!session.progress) session.progress = [];
    session.progress.push({ range, duration });
  });

const updateResult = (id: string, result: any) =>
  produce((draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    const session = draft.sessions.find(s => s.id === id);
    if (!session) return;
    session.result = result;
  });

const addSession = (session: UploadSession) =>
  produce((draft: WritableDraft<UploadProviderInfo & UploadProviderActions>) => {
    draft.sessions.push(session);
  });

// Defining this once here means we don't have to do it in every serialise call that uses this
// Regex are expensive to create so we want to avoid that
const IsSerialisedDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export const UploadProvider: FC<PropsWithChildren<UploadProviderProps>> = ({
  createUploadSession,
  uploadChunk,
  uploadFile,
  completeUpload,
  destroyUpload,
  options,
  children
}) => {
  const store = useMemo(
    () =>
      create(
        persist(
          devtools<UploadProviderInfo & UploadProviderActions>((set, get) => ({
            sessions: [],
            uploadFiles: async (files, args?: Record<string, any>) => {
              // Remember when the upload started
              set({ uploadStarted: new Date() });

              // We want to work in arrays, not FileLists but that is what we get from the input element
              // so to be nicer to the user we convert it here.
              files = Array.from(files);

              // Create a session for each file
              const uploads = await Promise.all(
                files.map<Promise<Upload>>(async file => {
                  // Create a session for the file
                  const session = await createUploadSession(file.name, file, args);
                  // Add the session to the store
                  set(state => addSession(session)(state));
                  // Return the session and the file
                  return [session, file];
                })
              );

              // Sort the uploads by session created date
              // This is just as a convenience to the user so they can see the uploads in the order they were added
              // Otherwise it just seems a bit random
              uploads.sort(([a], [b]) => a.created.getTime() - b.created.getTime());

              // Upload the files. We do this sequentially but we could do it in parallel in the future.
              for (const [session, file] of queuedUploads(uploads)) {
                try {
                  // If the file is small enough we can upload it in one go
                  if (file.size <= options.chunkedUploadThreshold) {
                    set(state => changeStatus(session.id, UploadStatus.Uploading)(state));
                    await uploadFile(session, file);
                    const result = await completeUpload(session);
                    set(state => updateResult(session.id, result)(state));
                    set(state => changeStatus(session.id, UploadStatus.Complete)(state));

                    continue;
                  }

                  // Otherwise we need to upload it in chunks
                  set(changeStatus(session.id, UploadStatus.Uploading));
                  for await (const chunk of readFileInChunks(file, { chunkSize: options.chunkSize })) {
                    // Check for abort
                    const { sessions } = get();
                    const current = sessions.find(s => s.id === session.id);
                    if (!current || current.status === UploadStatus.Cancelled) break;

                    // Upload the chunk
                    const range = { start: chunk.offset, end: chunk.offset + chunk.data.byteLength - 1 };
                    const start = Date.now();
                    await uploadChunk(session, chunk.data, range);
                    const duration = Date.now() - start;
                    set(state => updateProgress(session.id, range, duration)(state));
                  }
                  const result = await completeUpload(session);
                  set(state => updateResult(session.id, result)(state));
                  set(state => changeStatus(session.id, UploadStatus.Complete)(state));
                } catch (e) {
                  console.error(e);
                  set(state => changeStatus(session.id, UploadStatus.Failed)(state));
                }
              }
            },
            cancelUploadSession: async id => {
              const { sessions } = get();
              const session = sessions.find(s => s.id === id);
              if (!session) return;
              if (session.status === UploadStatus.Complete || session.status === UploadStatus.Cancelled) return;
              set(changeStatus(session.id, UploadStatus.Cancelled));
              await destroyUpload(session);
            },
            hideUploadSession: id => {
              set(state => ({ sessions: state.sessions.filter(s => s.id !== id) }));
            },
            hideOldUploads: () => {
              // Hide sessions older than an hour
              const now = new Date();
              set(state => ({ sessions: state.sessions.filter(s => now.getTime() - s.created.getTime() < 3600000) }));
            },
            clearCompletedSessions: () => {
              set(state => ({ sessions: state.sessions.filter(s => s.status !== UploadStatus.Complete) }));
            },
            retryUploadSession: async (id, file) => {
              const { sessions } = get();
              const session = sessions.find(s => s.id === id);
              if (!session) return;
              if (
                session.status === UploadStatus.Complete ||
                session.status === UploadStatus.Uploading ||
                session.status === UploadStatus.Cancelled
              ) {
                return;
              }

              // Perform the upload
              if (file.size <= options.chunkedUploadThreshold) {
                if (session.status !== UploadStatus.Waiting) {
                  throw new Error('Not implemented: retrying non-chunked, non-waiting uploads');
                }

                set(changeStatus(session.id, UploadStatus.Uploading));
                await uploadFile(session, file);
                const result = await completeUpload(session);
                set(updateResult(session.id, result));
                set(changeStatus(session.id, UploadStatus.Complete));

                return;
              }

              set(changeStatus(session.id, UploadStatus.Uploading));
              const progress = session.progress?.at(-1);
              const start = progress ? progress.range.end + 1 : 0;

              for await (const chunk of readFileInChunks(file, {
                chunkSize: options.chunkSize,
                initialOffset: start
              })) {
                // Check for abort
                const { sessions } = get();
                const current = sessions.find(s => s.id === session.id);
                if (!current || current.status === UploadStatus.Cancelled) break;

                // Upload the chunk
                const range = { start: chunk.offset, end: chunk.offset + chunk.data.byteLength - 1 };
                const start = Date.now();
                await uploadChunk(session, chunk.data, range);
                const duration = Date.now() - start;
                set(updateProgress(session.id, range, duration));
              }
              const result = await completeUpload(session);
              set(updateResult(session.id, result));
              set(changeStatus(session.id, UploadStatus.Complete));
            },
            identifyInterruptedUploads: () => {
              const { sessions } = get();
              sessions.forEach(session => {
                if (session.status === UploadStatus.Complete || session.status === UploadStatus.Cancelled) return;
                set(changeStatus(session.id, UploadStatus.Interrupted));
              });
            },
            retryUploadSessions: async list => {
              const files = Array.from(list);
              // Try and match incoming file to existing interrupted uploads
              const { sessions, retryUploadSession } = get();
              //
              const retryableSessions = sessions.filter(session => {
                // If the session is not interrupted then we can't retry it
                if (session.status !== UploadStatus.Interrupted) return false;
                // If we can't find a file that matches the session then we can't retry it
                const file = files.find(f => f.name === session.name && f.size === session.size);
                return !!file;
              });
              // Set the status of the sessions to waiting
              retryableSessions.map(session => set(changeStatus(session.id, UploadStatus.Waiting)));
              // Sort the sessions by created date
              const sortedSessions = retryableSessions.sort((a, b) => a.created.getTime() - b.created.getTime());
              for (const session of sortedSessions) {
                const file = files.find(f => f.name === session.name && f.size === session.size);
                if (file) {
                  await retryUploadSession(session.id, file);
                }
              }
            }
          })),
          {
            name: 'upload',
            storage: createJSONStorage(() => localStorage, {
              reviver: (key, value) =>
                // This is a ridiculously inefficient way of serialising dates but I didn't want to forget to specific a key in the future and everything break.
                // As long as we don't have an insane number of uploads this should be fine.
                typeof value === 'string' && IsSerialisedDate.test(value) ? new Date(value) : value
            }),
            version: 1
          }
        )
      ),
    [createUploadSession, uploadChunk, uploadFile, completeUpload, options, destroyUpload]
  );

  return (
    <UploadProviderContext.Provider value={store}>
      <UploadManager />
      {children}
    </UploadProviderContext.Provider>
  );
};

enum MigrationState {
  Unknown,
  Migrated
}

export const UploadManager = () => {
  // We need to identify any interrupted uploads and set them to the correct state
  // This is done on mount and then never again

  const identifyInterruptedUploads = useIdentifyInterruptedUploads();
  const [migrated, setMigrationState] = useState<MigrationState>(MigrationState.Unknown);

  useEffect(() => {
    if (migrated === MigrationState.Migrated) return;
    identifyInterruptedUploads();
    setMigrationState(MigrationState.Migrated);
  }, [identifyInterruptedUploads, setMigrationState, migrated]);

  return null;
};
